import React from 'react'
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Linking,
  TouchableWithoutFeedback,
} from 'react-native'
import { useTranslation, Trans } from 'react-i18next'
import { IconError, IconCloseSquareFilled } from '../../../../assets/icons'
import Button from './Button'
import { useResponsive } from '@linqpal/components/src/hooks'

type PaymentErrorModalProps = {
  onClose: () => void
  visible?: boolean
  isMobile?: boolean
}

const PaymentErrorModal: React.FC<PaymentErrorModalProps> = ({
  visible,
  onClose,
  isMobile,
}) => {
  const { t } = useTranslation('global')
  const { mobileWidth } = useResponsive()

  const modalWidth = isMobile ? mobileWidth - 30 : 602
  const modalHeight = isMobile ? 520 : 468
  const modalMinWidth = isMobile ? 330 : 500
  const textWrapperWidth = isMobile ? mobileWidth - 80 : 396
  const titleWidth = isMobile ? mobileWidth - 80 : 355
  const narrowMessageWidth = isMobile ? mobileWidth - 160 : 330
  const wideMessageWidth = isMobile ? mobileWidth - 60 : 450

  return (
    <Modal
      transparent
      // eslint-disable-next-line i18next/no-literal-string
      animationType="fade"
      visible={visible}
      onRequestClose={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.modalContainer,
              {
                width: modalWidth,
                minWidth: modalMinWidth,
                height: modalHeight,
              },
            ]}
          >
            {/* Close Button */}
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <IconCloseSquareFilled />
            </TouchableOpacity>

            {/* Error Icon */}
            <View
              style={[
                styles.iconWrapper,
                mobileWidth < 575 && { marginTop: 10, marginBottom: 28 },
              ]}
            >
              <IconError />
            </View>

            {/* Title */}
            <View style={[styles.textWrapper, { width: textWrapperWidth }]}>
              <Text
                style={[
                  styles.title,
                  { width: titleWidth },
                  mobileWidth < 575 && styles.mobileTitle,
                ]}
              >
                {t('integrations.pay-as-guest.payment-failed')}
              </Text>

              {/* Message */}
              <Text style={styles.message}>
                <View style={{ width: narrowMessageWidth }}>
                  <Text style={{ textAlign: 'center' }}>
                    {t('integrations.pay-as-guest.payment-error-again')}
                  </Text>
                </View>
                <View
                  style={{
                    width: wideMessageWidth,
                  }}
                >
                  <Text style={{ textAlign: 'center' }}>
                    <Trans
                      t={t}
                      i18nKey="integrations.pay-as-guest.contact-support"
                      components={{
                        email: (
                          <Text
                            onPress={() =>
                              Linking.openURL('mailto:support@bluetape.com')
                            }
                            style={styles.link}
                          >
                            support@bluetape.com
                          </Text>
                        ),
                      }}
                    />
                  </Text>
                </View>
              </Text>
            </View>

            {/* Retry Button */}
            <Button
              onPress={onClose}
              label={t('integrations.pay-as-guest.try-again')}
              buttonStyle={styles.button}
            />
          </View>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  )
}

const styles = StyleSheet.create({
  modalOverlay: {
    backgroundColor: 'rgba(0, 51, 83, 0.2)',
    alignItems: 'center',
    flex: 1,
  },
  modalContainer: {
    backgroundColor: 'white',
    padding: 18,
    alignItems: 'center',
    borderRadius: 12,
    alignSelf: 'center',
    justifyContent: 'flex-start',
    marginTop: 80,
  },
  closeButton: {
    alignSelf: 'flex-end',
    marginBottom: 14,
  },
  iconWrapper: {
    marginBottom: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    fontFamily: 'Inter',
    lineHeight: 32,
    color: '#19262F',
    textAlign: 'center',
    marginBottom: 12,
  },
  mobileTitle: {
    fontSize: 20,
  },
  message: {
    fontSize: 16,
    color: '#19262F',
    fontFamily: 'Inter',
    textAlign: 'center',
    marginBottom: 32,
    fontWeight: '400',
    lineHeight: 28,
  },
  narrowMessage: {
    textAlign: 'center',
  },
  wideMessage: {
    textAlign: 'center',
  },
  link: {
    fontWeight: '700',
    color: '#00A0F3',
  },
  button: {
    width: 171,
  },
})

export default PaymentErrorModal
